import React from 'react';
import 'chart.js/auto';
import { Doughnut } from 'react-chartjs-2';
import { RiskBucket, LicenseTypeStat } from 'api/ExportHubAPI';

/**
 * Dashboard charts (spec §9/§5.3) — chart.js via react-chartjs-2, themed from
 * CSS variables so both light/dark work. Doughnuts for Risk Distribution and
 * license-type breakdown.
 */

const cssVar = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() ||
  '#4895ef';

const RISK_VAR: Record<string, string> = {
  Low: '--full-bar-color',
  Medium: '--mid-color',
  High: '--low-color',
  Critical: '--low-color',
  Unscreened: '--text-tertiary',
};

const TYPE_PALETTE = [
  '--action-color',
  '--full-bar-color',
  '--mid-color',
  '--count-color',
  '--accent-color',
  '--low-color',
];

const RISK_ORDER = ['Critical', 'High', 'Medium', 'Low', 'Unscreened'];

const doughnutOptions = (): Record<string, unknown> => ({
  responsive: true,
  maintainAspectRatio: false,
  cutout: '62%',
  plugins: {
    legend: {
      position: 'right',
      labels: {
        color: cssVar('--text-secondary'),
        font: { family: 'Inter', size: 12 },
        boxWidth: 12,
        padding: 10,
      },
    },
  },
});

export const RiskDistributionChart: React.FC<{ buckets: RiskBucket[] }> = ({
  buckets,
}) => {
  const ordered = [...buckets].sort(
    (a, b) => RISK_ORDER.indexOf(a.risk) - RISK_ORDER.indexOf(b.risk),
  );
  const total = ordered.reduce((n, b) => n + b.count, 0);
  if (total === 0) {
    return <div className='eh-detail-note'>No screening data yet.</div>;
  }
  const data = {
    labels: ordered.map((b) => `${b.risk} (${b.count})`),
    datasets: [
      {
        data: ordered.map((b) => b.count),
        backgroundColor: ordered.map((b) => cssVar(RISK_VAR[b.risk] || '--text-tertiary')),
        borderColor: cssVar('--bg-colors'),
        borderWidth: 2,
      },
    ],
  };
  return (
    <div className='eh-chart-box'>
      <Doughnut data={data} options={doughnutOptions()} />
    </div>
  );
};

export const LicenseTypeChart: React.FC<{ data: LicenseTypeStat[] }> = ({
  data,
}) => {
  const rows = data.filter((d) => d.count > 0);
  if (rows.length === 0) {
    return <div className='eh-detail-note'>No licenses yet.</div>;
  }
  const chartData = {
    labels: rows.map((d) => `${d.license_type} (${d.count})`),
    datasets: [
      {
        data: rows.map((d) => d.count),
        backgroundColor: rows.map((_d, i) =>
          cssVar(TYPE_PALETTE[i % TYPE_PALETTE.length]),
        ),
        borderColor: cssVar('--bg-colors'),
        borderWidth: 2,
      },
    ],
  };
  return (
    <div className='eh-chart-box'>
      <Doughnut data={chartData} options={doughnutOptions()} />
    </div>
  );
};
